import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import { CardString } from 'pokersolver'
import { ModalProps } from 'react-native'

import { Deck, GAME_CONFIG } from '@ppwr/poker'

import { GameSnapshotModel } from '../api-store'
import { withRootStore } from '../extensions'

export const GameModalKinds = {
  allIn: 'allIn',
  bet: 'bet',
  raise: 'raise',
  buy: 'buy',
  leaveGame: 'leaveGame',
  winningHand: 'winningHand',
  options: 'options',
  joinVideo: 'joinVideo',
  pot: 'pot',
  gameDeleted: 'gameDeleted',
  sidepotPlayers: 'sidepotPlayers',
  gameStartTimer: 'gameStartTimer',
  gamePauseTimer: 'gamePauseTimer',
  teacherOptions: 'teacherOptions',
  terminateGame: 'terminateGame',
  playerOutOfChips: 'playerOutOfChips',
  leaderBoard: 'leaderBoard',
  support: 'support',
  endPracticeGame: 'endPracticeGame',
  handWinnersModal: 'handWinnersModal',
  appPermission: 'appPermission',
  threeCardStart: 'threeCardStart',
  threeCardLeaveConfirm: 'threeCardLeaveConfirm',
  removedByTeacher: 'removedByTeacher'
} as const
export type GameModalKind = keyof typeof GameModalKinds

export enum LayerType {
  alert = 'alert',
  toast = 'toast',
  activityIndicator = 'activityIndicator',
  prompts = 'prompts',
  messages = 'messages',
  screen = 'screen'
}

export const BaseGameWinner = types.model('BaseGameWinner').props({
  winningText: types.optional(types.string, ''),
  chipsWon: types.optional(types.number, 0),
  holeCards: types.optional(types.array(types.enumeration<CardString>(new Deck().buildDeck())), []),
  winningCards: types.optional(types.array(types.enumeration<CardString>(new Deck().buildDeck())), [])
})

export const GameWinner = BaseGameWinner.named('GameWinner').props({
  mainPot: types.optional(types.number, 0),
  sidePot: types.maybe(types.frozen())
})

export const PotWinner = BaseGameWinner.named('PotWinner').props({
  potKind: types.optional(types.string, ''),
  rankedHandName: types.optional(types.string, ''),
  playerName: types.optional(types.string, ''),
  playerId: types.optional(types.string, '')
})

export const GameModal = types.model('GameModal').props({
  game: types.maybe(GameSnapshotModel),
  props: types.frozen<ModalProps>(),
  kind: types.enumeration<GameModalKind>('GameModalKind', Object.values(GameModalKinds)),
  layerType: types.enumeration<LayerType>('LayerType', Object.values(LayerType)),
  isBlocking: types.optional(types.boolean, false),
  timeToKeepOpen: types.optional(types.number, 0),
  gameWinner: types.optional(GameWinner, {}),
  message: types.optional(types.string, ''),
  potWinners: types.optional(types.array(PotWinner), [])
})

export type GameModalType = Instance<typeof GameModal>
export type GameWinnerType = Instance<typeof GameWinner>
export type PotWinnerType = Instance<typeof PotWinner>
export type PotWinnerSnapshot = SnapshotOut<typeof PotWinner>

export const GameModalQueue = types
  .model('GameModalQueue')
  .props({
    modalQueue: types.optional(types.array(GameModal), []),
    playerActionTimerExpired: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .views((self) => ({
    getFirstGameModal(): GameModalType | undefined {
      // Game deleted modal should take priority over all other modals
      const gameDeletedModal = self.modalQueue.find((modal) => modal.kind === GameModalKinds.gameDeleted)

      if (gameDeletedModal) {
        return gameDeletedModal
      }

      if (self.modalQueue.length > 0) {
        return self.modalQueue[0]
      }
      return undefined
    }
  }))
  .actions((self) => ({
    pushGameModals(data: GameModalType) {
      return self.modalQueue.push(GameModal.create(data))
    },
    shiftGameModals() {
      return self.modalQueue.shift()
    },
    clearGameModalQueue() {
      self.modalQueue.clear()
    },
    setPlayerActionTimerExpired(expired: boolean) {
      self.playerActionTimerExpired = expired
    },
    createBuyPrompt() {
      const buyModalAlreadyExists = self.modalQueue.some((modal) => modal.kind === GameModalKinds.buy)
      if (!buyModalAlreadyExists) {
        self.modalQueue.push(
          GameModal.create({
            props: {},
            kind: GameModalKinds.buy,
            isBlocking: false,
            layerType: LayerType.prompts,
            timeToKeepOpen: 0
          })
        )
      }
    },
    createEndPracticeGamePrompt() {
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.endPracticeGame,
          isBlocking: false,
          layerType: LayerType.prompts,
          timeToKeepOpen: 0
        })
      )
    },
    createAllInModal() {
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.allIn,
          isBlocking: false,
          layerType: LayerType.messages,
          timeToKeepOpen: 5
        })
      )
    },
    createGameDeletedAlert() {
      self.rootStore.game.teardown()
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.gameDeleted,
          isBlocking: true,
          layerType: LayerType.messages,
          timeToKeepOpen: 0
        })
      )
    },
    createAppPermission() {
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.appPermission,
          isBlocking: false,
          layerType: LayerType.prompts,
          timeToKeepOpen: 0
        })
      )
    },
    createSidepotPlayer(message: string) {
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.sidepotPlayers,
          isBlocking: false,
          layerType: LayerType.alert,
          timeToKeepOpen: 5,
          message: message
        })
      )
    },
    createPlayerOutOfChipsModal() {
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.playerOutOfChips,
          isBlocking: false,
          layerType: LayerType.messages,
          timeToKeepOpen: 5
        })
      )
    },
    createHandWinnersModal(potWinners: PotWinnerSnapshot[]) {
      //NOTE: Ensure that we're not pushing the empty potwinner
      if (potWinners.length > 0) {
        self.modalQueue.push(
          GameModal.create({
            props: {},
            kind: GameModalKinds.handWinnersModal,
            isBlocking: false,
            layerType: LayerType.messages,
            timeToKeepOpen: GAME_CONFIG.DELAY_BETWEEN_HANDS,
            potWinners
          })
        )
      }
    },
    createRemovedByTeacherModal() {
      self.modalQueue.push(
        GameModal.create({
          props: {},
          kind: GameModalKinds.removedByTeacher,
          isBlocking: true,
          layerType: LayerType.messages,
          timeToKeepOpen: 0
        })
      )
    }
    // createGameStartTimerModal(time?: number) {
    //   self.modalQueue.push(
    //     GameModal.create({
    //       props: {},
    //       kind: GameModalKind.gameStartTimer,
    //       isBlocking: false,
    //       layerType: LayerType.alert,
    //       timeToKeepOpen: time ? time : 60,
    //     })
    //   )
    // },
    // createGamePauseTimerModal(time?: number) {
    //   self.modalQueue.push(
    //     GameModal.create({
    //       props: {},
    //       kind: GameModalKind.gamePauseTimer,
    //       isBlocking: false,
    //       layerType: LayerType.alert,
    //       timeToKeepOpen: time ? time : 30,
    //     })
    //   )
    // },
  }))

/**
 * The GameModalQueueStore instance.
 */
export type GameModalQueueStore = Instance<typeof GameModalQueue>

/**
 * The data of a GameModalQueueStore.
 */
export type GameModalQueueStoreSnapshot = SnapshotOut<typeof GameModalQueue>
